import type { ComponentType } from "react";
import { IntroducingPagesHero } from "./illustrations";

/**
 * Registry of in-app feature announcements.
 *
 * One entry is one announcement: its copy, audience, lifetime, and how it
 * renders. Shipping an announcement is adding an entry here (plus an
 * illustration scene when the display calls for one); stale entries retire
 * themselves through `expiresAt` and get deleted in a cleanup PR.
 *
 * Three displays, in decreasing order of interruption:
 *
 * - `modal`: app-level dialog over the whole product, mounted once in
 *   MainLayout. Major releases only, and at most one per session.
 * - `banner`: full-width bar a host screen mounts at the top of its content.
 * - `card`: inline card a host screen mounts inside its layout.
 *
 * Walkthrough and Hotspot (`@/components/walkthrough`) stay the tools for
 * teaching a surface in place; announcements are for telling users something
 * changed at all.
 *
 * QA: `?announcement=<id>` (or `?announcement=1`) forces an entry to render
 * regardless of dates, dismissal, or audience.
 */

export type AnnouncementDisplay = "modal" | "banner" | "card";

/** Slots that can host an announcement. Modals always use `app`. */
export type AnnouncementPlacement = "app" | "pages-list";

/**
 * Audience context a host passes from data it already has. Every field is
 * optional so hosts only provide what their announcements check.
 */
export type AnnouncementContext = {
  /** The Pages surface (`helios-pages` flag) is enabled for this user. */
  pagesEnabled?: boolean;
  /** Types of the gym's active integrations (for example `mariana-tek`). */
  integrations?: string[];
  /**
   * An active Mindbody integration has `options.expandedWebhookEvents`.
   * Recurring catalog and class-schedule ingest only run for those gyms.
   */
  mindbodyExpandedWebhookEvents?: boolean;
  /** The gym has at least one form (a Forms-era gym). */
  hasForms?: boolean;
};

export type FeatureAnnouncementEntry = {
  /** Stable kebab-case id: the analytics and dismissal key. */
  id: string;
  display: AnnouncementDisplay;
  /** The only slot that may render this entry. */
  placement: AnnouncementPlacement;
  title: string;
  body: string;
  /**
   * The call to action. Clicking it completes the announcement. Internal
   * hrefs (starting with `/`) navigate in-app; anything else opens a new tab.
   */
  primaryAction: { label: string; href: string };
  /** Label for the dismiss button. Modals only; defaults to "Not now". */
  secondaryLabel?: string;
  /**
   * Miniature product scene. Modal heroes render the full dialog header
   * (height, background, bottom border); card art is a small inline scene.
   */
  illustration?: ComponentType;
  /** ISO date; the entry never renders before this day. */
  publishedAt: string;
  /**
   * ISO date; the entry never renders on or after this day. Default to 60
   * days after `publishedAt`: an announcement is news, not furniture.
   */
  expiresAt: string;
  /** Audience predicate over the host's context. Pure and synchronous. */
  eligible: (ctx: AnnouncementContext) => boolean;
};

export const ANNOUNCEMENTS: FeatureAnnouncementEntry[] = [
  {
    id: "introducing-pages",
    display: "modal",
    placement: "app",
    title: "Introducing Pages",
    body: "Build landing pages that sell your intro offer, memberships, and schedule from one link. Prices and schedules sync from your booking system, every visit and checkout is tracked, and your forms now live in Pages too.",
    primaryAction: { label: "Explore Pages", href: "/pages" },
    secondaryLabel: "Not now",
    illustration: IntroducingPagesHero,
    publishedAt: "2026-08-15",
    expiresAt: "2026-10-14",
    eligible: (ctx) =>
      ctx.pagesEnabled === true &&
      (ctx.integrations?.includes("mariana-tek") === true ||
        ctx.integrations?.includes("fitdegree") === true ||
        (ctx.integrations?.includes("mindbody") === true &&
          ctx.mindbodyExpandedWebhookEvents === true)),
  },
];
